import { create } from "zustand"
import type {
  ChatReference,
  IssueAttachment,
  IssueStatus,
} from "../types/assistant.types"

export type SubmitMode = "idle" | "armed"
export type ChatView = "chat" | "history"

interface ChatPanelState {
  isOpen: boolean
  view: ChatView
  threadId: number | null
  threadStatus: IssueStatus | null
  submitMode: SubmitMode
  hasAssistantReplied: boolean
  pendingAttachments: IssueAttachment[]
  pendingReferences: ChatReference[]
  /** ``true`` once ``ThreadLoader`` has finished its initial fetch
   *  (or determined no draft exists). Composer logic that reads from
   *  the loaded conversation — like the auto page-context chip —
   *  waits for this so it doesn't fire BEFORE the runtime is populated
   *  and end up adding a chip the conversation already references.
   *  Reset on every panel close so a reopen re-derives. */
  threadHydrated: boolean
  /** Bumped whenever the panel resets to a fresh state. The
   *  ``IssueReportPanel`` keys its ``AssistantRuntimeProvider`` on
   *  this value so the assistant-ui local runtime gets a real
   *  unmount + remount. Without that, the runtime instance survives
   *  across sheet open/close (the panel itself is still in the app
   *  tree) and the old conversation tree gets visually grafted onto
   *  the next chat. */
  sessionId: number
  open: () => void
  close: () => void
  toggle: () => void
  setView: (view: ChatView) => void
  setThreadId: (id: number) => void
  setThreadStatus: (status: IssueStatus | null) => void
  setThreadHydrated: (hydrated: boolean) => void
  clearThread: () => void
  setSubmitMode: (mode: SubmitMode) => void
  markAssistantReplied: () => void
  addAttachment: (attachment: IssueAttachment) => void
  removeAttachment: (id: number) => void
  removeAttachments: (ids: number[]) => void
  clearAttachments: () => void
  addReference: (reference: ChatReference) => void
  removeReference: (key: string) => void
  setReferences: (references: ChatReference[]) => void
  clearReferences: () => void
}

const _FRESH_THREAD_STATE = {
  threadId: null,
  threadStatus: null,
  submitMode: "idle" as const,
  hasAssistantReplied: false,
  pendingAttachments: [] as IssueAttachment[],
  pendingReferences: [] as ChatReference[],
  threadHydrated: false,
  view: "chat" as const,
}

function _closeWithMaybeReset(s: ChatPanelState) {
  // Once a thread has been submitted or abandoned, leaving the panel
  // is a "I'm done with this" signal — reopening should start a
  // fresh chat rather than carry the OPEN/ABANDONED issue id forward
  // (the chat endpoint refuses non-DRAFT threads, so a stray seed
  // message like "I'd like to report a problem" would otherwise
  // round-trip an "already submitted" error). DRAFT closes preserve
  // state so a mid-conversation user can pick up where they left off.
  //
  // ``sessionId`` is bumped so the panel can re-key its
  // AssistantRuntimeProvider — clearing the runtime's message tree
  // is not enough on its own, since assistant-ui's local runtime
  // survives across sheet close/open and would otherwise graft the
  // old conversation onto the new chat.
  //
  // ``threadHydrated`` resets in BOTH branches so the next reopen
  // re-derives auto-attach state once ``ThreadLoader`` has refilled
  // the runtime. Without resetting here, a stale ``true`` would let
  // the composer auto-attach the page-context order before
  // ``ThreadLoader`` had a chance to repopulate the conversation.
  if (s.threadStatus != null && s.threadStatus !== "DRAFT") {
    return { isOpen: false, ..._FRESH_THREAD_STATE, sessionId: s.sessionId + 1 }
  }
  return { isOpen: false, threadHydrated: false }
}

function _refKey(r: ChatReference): string {
  return `${r.type}:${r.id}`
}

export const useChatPanel = create<ChatPanelState>((set) => ({
  isOpen: false,
  view: "chat",
  threadId: null,
  threadStatus: null,
  submitMode: "idle",
  hasAssistantReplied: false,
  pendingAttachments: [],
  pendingReferences: [],
  threadHydrated: false,
  sessionId: 0,
  open: () => set({ isOpen: true }),
  close: () => set(_closeWithMaybeReset),
  toggle: () =>
    set((s) => (s.isOpen ? _closeWithMaybeReset(s) : { isOpen: true })),
  setView: (view) => set({ view }),
  setThreadId: (id) => set({ threadId: id }),
  setThreadStatus: (threadStatus) => set({ threadStatus }),
  setThreadHydrated: (threadHydrated) => set({ threadHydrated }),
  clearThread: () =>
    set((s) => ({
      threadId: null,
      threadStatus: null,
      submitMode: "idle",
      hasAssistantReplied: false,
      pendingAttachments: [],
      pendingReferences: [],
      threadHydrated: false,
      view: "chat",
      sessionId: s.sessionId + 1,
    })),
  setSubmitMode: (mode) => set({ submitMode: mode }),
  markAssistantReplied: () => set({ hasAssistantReplied: true }),
  addAttachment: (attachment) =>
    set((s) => ({ pendingAttachments: [...s.pendingAttachments, attachment] })),
  removeAttachment: (id) =>
    set((s) => ({
      pendingAttachments: s.pendingAttachments.filter((a) => a.id !== id),
    })),
  removeAttachments: (ids) => {
    if (!ids.length) return
    const idSet = new Set(ids)
    set((s) => ({
      pendingAttachments: s.pendingAttachments.filter((a) => !idSet.has(a.id)),
    }))
  },
  clearAttachments: () => set({ pendingAttachments: [] }),
  addReference: (reference) =>
    set((s) => {
      const key = _refKey(reference)
      if (s.pendingReferences.some((r) => _refKey(r) === key)) return s
      return { pendingReferences: [...s.pendingReferences, reference] }
    }),
  removeReference: (key) =>
    set((s) => ({
      pendingReferences: s.pendingReferences.filter((r) => _refKey(r) !== key),
    })),
  setReferences: (references) => set({ pendingReferences: references }),
  clearReferences: () => set({ pendingReferences: [] }),
}))

export { _refKey as refKey }
